import type { IShowCatalog, IShowCatalogProduct } from '@pkg/lib-data'
import { EUserJourneyType } from '@pkg/lib-data'

import { defineImages, generateImageAlt, priceFormatter } from '../utils'
import type { ProductDetailsDataShows } from './details'

export interface ProductCardDataShows {
  type: EUserJourneyType.SHOWS
  api: IShowCatalogProduct
  computed: {
    href: string
    imageUrls: any[] | null
    totalPrice: number
    totalPriceFormatted: string
    currencySymbol: string
    currencyIso: string
    resourceLevel: number
    marketingLabel?: string
    dateLabelPrimary: string
    dateLabelSecondary: string
    eventDateFormatted: string
    id: string
    title: string
    expandedTitle: string
    locationLabel: string
    address?: string
    soldOut: boolean
    date: string
    time: string
  }
}

export interface ProductCardDataShowsArgs {
  catalogsContentData: IShowCatalog[]
  categoryId: string
  productsMapFiltered: Record<string, ProductDetailsDataShows>
  productsMapOriginal: Record<string, ProductDetailsDataShows>
}

// Simplified path generation for widget
const getProductPath = (productId: string, categoryId: string) => {
  if (typeof window === 'undefined') {
    return `/product?id=${productId}&category=${categoryId}&step=show-details`
  }

  const origin = window.location.origin

  return `${origin}/product?id=${productId}&category=${categoryId}&step=show-details`
}

export const defineProductCardDataShows = (args: ProductCardDataShowsArgs): { default: ProductCardDataShows[] } => {
  const { categoryId, productsMapFiltered, catalogsContentData } = args

  const productCards: ProductCardDataShows[] = []

  for (const catalogContentItem of catalogsContentData) {
    for (const productCard of Object.values(catalogContentItem.products)) {
      const primaryItem = productsMapFiltered[productCard.id] as ProductDetailsDataShows | undefined

      if (!primaryItem) {
        continue
      }

      const currencySymbol = primaryItem.computed.currencySymbol

      const cardData: ProductCardDataShows = {
        type: EUserJourneyType.SHOWS,
        api: productCard,
        computed: {
          href: getProductPath(productCard.id, categoryId),
          imageUrls: defineImages({
            imageUrls: [
              {
                imageUrl: productCard.imageUrl,
                alt: productCard.imageAlt || generateImageAlt(productCard.name || '', productCard.areaName || ''),
              },
            ],
          }),
          totalPrice: Math.trunc(primaryItem.api.sellingPrice),
          totalPriceFormatted: priceFormatter(primaryItem.api.sellingPrice),
          currencySymbol,
          currencyIso: primaryItem.computed.currencyIso,
          resourceLevel: primaryItem.computed.resourceLevel,
          marketingLabel: primaryItem.computed.dealTicker,
          dateLabelPrimary: primaryItem.computed.dateLabelPrimary,
          dateLabelSecondary: primaryItem.computed.dateLabelSecondary,
          eventDateFormatted: primaryItem.computed.eventDateFormatted,
          id: productCard.id,
          title: primaryItem.computed.title,
          expandedTitle: '',
          locationLabel: primaryItem.computed.placeName,
          address: primaryItem.computed.locationLabel,
          soldOut: primaryItem.computed.soldOut,
          date: productCard.date,
          time: productCard.time,
        },
      }

      productCards.push(cardData)
    }
  }

  return { default: productCards }
}
